import { NewsType } from '../NewsMedia';

// sample content for the add / edit pages: what "Get Details" fills in, and what /edit opens with (no hub API exists yet)
export type LinkType = Exclude<NewsType, 'article'>;

export interface LinkSample {
    url: string;
    title: string;
    // the grey line in the "Details found" box
    found: string;
    // the three fields under the title, in the type's own order (see LINK_FIELDS)
    fields: [string, string, string];
    description: string;
    quote?: string;
    // sub line of "Picture taken from the link"
    pictureFrom: string;
}

export const getLinkSample = (type: LinkType, short: string): LinkSample => {
    const samples: Record<LinkType, LinkSample> = {
        podcast: {
            url: 'https://juniorminer.example/podcast/49-metals-episode-214',
            title: `${short} on the Junior Miner podcast — Episode 214`,
            found: 'Junior Miner Podcast · Episode 214 · 38 min · published 28 Aug 2026',
            fields: ['Junior Miner', '28 Aug 2026', '38 min'],
            description: 'Managing Director Wallace Turner talks through the Gold Mountain assay results and what’s next for the Nevada portfolio.',
            pictureFrom: 'Junior Miner episode artwork',
        },
        video: {
            url: 'https://www.youtube.com/watch?v=Xk29fQ7Lm3E',
            title: 'Sinter Project field program update',
            found: `YouTube · ${short} channel · 4:32 · uploaded 26 Aug 2026`,
            fields: ['YouTube', '26 Aug 2026', '4 min 32 s'],
            description: 'Exploration Manager Megan Cole walks through the first week of mapping and sampling at the Sinter Project in Nevada.',
            pictureFrom: 'YouTube thumbnail',
        },
        webinar: {
            url: 'https://us02web.zoom.us/webinar/register/WN_49M-sept-update',
            title: 'September investor webinar — Gold Mountain results & next steps',
            found: 'Zoom webinar · registration page · Tue 8 Sep 2026, 11:00 am AWST',
            fields: ['Tue 8 Sep 2026 · 11:00 am AWST', 'Wallace Turner, Managing Director', '45 min + Q&A'],
            description: 'Join Wallace Turner for a 45‑minute update on the Gold Mountain assay results, the Sinter field program and what to expect this quarter. Questions welcome.',
            pictureFrom: 'Zoom registration banner',
        },
        coverage: {
            url: 'https://dailyledger.example/business/mining/nevadas-next-gold-story',
            title: '“Nevada’s next gold story” — The Daily Ledger',
            found: 'The Daily Ledger · Business · by Jordan Blake · 9 Aug 2026',
            fields: ['The Daily Ledger', 'Jordan Blake', '9 Aug 2026'],
            quote: `“${short} has quietly assembled one of the most interesting gold land packages in Nevada.”`,
            description: 'Feature on the Gold Mountain discovery and the company’s Nevada strategy.',
            pictureFrom: 'Photo from the article',
        },
    };
    return samples[type];
};

export interface ArticleSample {
    headline: string;
    category: string;
    date: string;
    summary: string;
    // editor HTML, built by ArticleEditor so the embedded video matches what "Add video" inserts
    paragraphs: [string, string];
}

export const getArticleSample = (short: string): ArticleSample => ({
    headline: `${short} featured on the Junior Miner podcast`,
    category: 'Media coverage',
    date: '28 Aug 2026',
    summary: 'Managing Director Wallace Turner joined the Junior Miner team to discuss the Gold Mountain assay results and what’s next for the Nevada portfolio.',
    paragraphs: [
        `${short} Managing Director Wallace Turner was a guest on this week’s Junior Miner podcast, talking through the recent high-grade assay results from Gold Mountain and the company’s plans for the September quarter.`,
        'Listen to the full episode above, or read the transcript on the Junior Miner website.',
    ],
});

export const ARTICLE_CATEGORIES = ['Company news', 'Exploration update', 'Media coverage', 'Corporate', 'Community'];

// "28 Aug 2026" — how the add pages print a date
export const shortDate = (d: Date) => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }).replace(/\./g, '');
